import { useCallback, useEffect, useRef, useState } from 'react';
import { fmtDateTime } from '../../../../utils/ruDates';
import './OzonAutomationRk.css';

const number = value => (value == null ? '—' : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 }));
const failure = err => (typeof err?.detail === 'string' ? err.detail : err?.message || 'Не удалось выполнить запрос');
const toPercent = value => (value == null || value === '' ? '' : Math.round(Number(value) * 1e6) / 1e4);
const toFraction = value => (value === '' || value == null ? null : Math.round(Number(value) * 1e4) / 1e6);
const num = value => (value === '' || value == null ? null : Number(value));
const clone = value => JSON.parse(JSON.stringify(value));
const pct = value => (value == null ? '—' : `${Math.round(value * 100)}%`);

const TYPES = {
  search: { placement: 'PLACEMENT_TOP_PROMOTION', title: 'Поиск' },
  rec: { placement: 'PLACEMENT_SEARCH_AND_CATEGORY', title: 'Поиск и Рекомендации' },
};
const STATUS = {
  normal: ['Норма', 'ok'], attention: ['Внимание', 'warn'], cannibalism: ['Каннибализм', 'bad'], unknown: ['Мало данных', 'muted'],
};
const SEVERAL_KEYS = 'Несколько ключевых запросов в «Удержании позиции» могут снижать качество главного ключа: запросы расставлены по приоритету сверху вниз, при конфликте выигрывает верхний.';
const HOLD_ACTIONS = { wait: 'Ждём', keep: 'Не трогаем', move: 'Меняем', released: 'Отдана «Авто»' };
const ZONE_TITLE =index => (index === 0 ? '1 · высший' : `${index + 1} · приоритет −${index}`);

// Форма <-> настройки на сервере: ДРР на экране в процентах, на сервере долей.
const blockToForm = block => ({ ...block, drr_ad_max: toPercent(block.drr_ad_max), drr_total_max: toPercent(block.drr_total_max) });
const blockToWire = block => ({
  ...block, bid_min: num(block.bid_min), bid_max: num(block.bid_max),
  drr_ad_max: toFraction(block.drr_ad_max), drr_total_max: toFraction(block.drr_total_max),
});
const toForm = settings => ({
  search: blockToForm(settings.search), rec: blockToForm(settings.rec),
  campaigns: Object.fromEntries(Object.entries(settings.campaigns || {}).map(([cid, entry]) => [cid, blockToForm(entry)])),
  anticannibal: settings.anticannibal,
});
const toWire = form => ({
  search: blockToWire(form.search), rec: { ...blockToWire(form.rec), mode: 'AUTO' },
  campaigns: Object.fromEntries(Object.entries(form.campaigns).map(([cid, entry]) => [cid, blockToWire(entry)])),
  anticannibal: form.anticannibal,
});

const range = (from, to) => {
  if (from == null && to == null) return '—';
  if (from == null) return `до ${number(to)}`;
  return to == null ? `от ${number(from)}` : `${number(from)}–${number(to)}`;
};

function Field({ label, children, wide = false }) {
  return <label className={`rk-field${wide ? ' is-wide' : ''}`}><span>{label}</span>{children}</label>;
}

function Check({ checked, onChange, children, hint }) {
  return <label className='rk-check'><input type='checkbox' checked={checked} onChange={onChange} /><span>{children}{hint && <small>{hint}</small>}</span></label>;
}

function LimitFields({ block, onChange }) {
  const set = key => e => onChange({ [key]: e.target.value });
  return <>
    <Field label='Минимальная ставка, ₽'><input type='number' min='0' value={block.bid_min ?? ''} onChange={set('bid_min')} placeholder='без предела' /></Field>
    <Field label='Максимальная ставка, ₽'><input type='number' min='0' value={block.bid_max ?? ''} onChange={set('bid_max')} placeholder='без предела' /></Field>
    <Field label='Предельный ДРР рекламный, %'><input type='number' min='0' value={block.drr_ad_max ?? ''} onChange={set('drr_ad_max')} placeholder='без предела' /></Field>
    <Field label='Предельный ДРР общий, %'><input type='number' min='0' value={block.drr_total_max ?? ''} onChange={set('drr_total_max')} placeholder='без предела' /></Field>
  </>;
}

// Список запросов: порядок = приоритет, первый — главный (для ключей «Удержания»).
function Keywords({ items, max, onChange, mainFirst = false }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const text = draft.trim();
    if (!text || items.includes(text) || items.length >= max) return;
    onChange([...items, text]);
    setDraft('');
  };
  const move = (index, delta) => {
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(index + delta, 0, moved);
    onChange(next);
  };
  return <div className='rk-keywords'>
    <ol>{items.map((item, index) => <li key={item}>
      <span className='rk-keywords__text'>{item}</span>
      {mainFirst && <em className={index === 0 ? 'is-main' : ''}>{index === 0 ? 'главный' : 'вспомогательный'}</em>}
      <button type='button' disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Выше: ${item}`}>↑</button>
      <button type='button' disabled={index === items.length - 1} onClick={() => move(index, 1)} aria-label={`Ниже: ${item}`}>↓</button>
      <button type='button' onClick={() => onChange(items.filter((_, i) => i !== index))} aria-label={`Убрать: ${item}`}>×</button>
    </li>)}</ol>
    {items.length < max && <div className='rk-keywords__add'>
      <input value={draft} onChange={e => setDraft(e.target.value)} placeholder='Новый ключевой запрос'
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      <button type='button' onClick={add} disabled={!draft.trim()}>Добавить</button>
      <small>{items.length} из {max}</small>
    </div>}
  </div>;
}

// Зоны РК: диапазон ставок ↔ диапазон мест, по приоритету. Таблица с подписанными колонками, а не четыре числа подряд.
function Zones({ zones, onChange }) {
  const patchZone = (index, values) => onChange(zones.map((zone, i) => (i === index ? { ...zone, ...values } : zone)));
  const move = (index, delta) => {
    const next = [...zones];
    const [moved] = next.splice(index, 1);
    next.splice(index + delta, 0, moved);
    onChange(next);
  };
  const cell = (index, key, label) => <td><input type='number' min='0' aria-label={`${label}: зона ${index + 1}`}
    value={zones[index][key] ?? ''} onChange={e => patchZone(index, { [key]: e.target.value === '' ? null : Number(e.target.value) })} /></td>;
  return <div className='rk-zones'>
    {zones.length > 0 && <table aria-label='Зоны'>
      <thead><tr><th>Приоритет</th><th>Ставка от, ₽</th><th>Ставка до, ₽</th><th>Место от</th><th>Место до</th><th>Тянуться к</th><th /></tr></thead>
      <tbody>{zones.map((zone, index) => <tr key={index}>
        <th scope='row'>Зона {ZONE_TITLE(index)}</th>
        {cell(index, 'bid_min', 'ставка от, ₽')}{cell(index, 'bid_max', 'ставка до, ₽')}
        {cell(index, 'pos_from', 'место от')}{cell(index, 'pos_to', 'место до')}
        <td><select aria-label={`Направление: зона ${index + 1}`} value={zone.lean || 'DOWN'} onChange={e => patchZone(index, { lean: e.target.value })}>
          <option value='UP'>верху диапазона</option><option value='MID'>середине диапазона</option><option value='DOWN'>низу (экономить)</option>
        </select></td>
        <td className='rk-actions'>
          <button type='button' disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Зона выше: ${index + 1}`}>↑</button>
          <button type='button' disabled={index === zones.length - 1} onClick={() => move(index, 1)} aria-label={`Зона ниже: ${index + 1}`}>↓</button>
          <button type='button' onClick={() => onChange(zones.filter((_, i) => i !== index))} aria-label={`Убрать зону ${index + 1}`}>×</button>
        </td>
      </tr>)}</tbody>
    </table>}
    {zones.length < 3 && <button type='button' onClick={() => onChange([...zones, { bid_min: null, bid_max: null, pos_from: null, pos_to: null, lean: 'DOWN' }])}>
      Добавить зону ({zones.length} из 3)</button>}
  </div>;
}

function PositionReport({ report, agentOnline }) {
  return <div className='rk-report' aria-label='Замеры и вероятность попадания в зоны'>
    {!agentOnline && <p className='rk-note is-action'>Локальный агент не на связи: замеры позиции не идут, РК работает как «Авто», пока агент не вернётся.</p>}
    {!report
      ? <p className='rk-muted'>Замеров пока нет: агент меряет ключи раз в полчаса, пока он на связи.</p>
      : <>
        <p className='rk-report__head'><strong>Замеров за 7 дней: {report.samples}</strong>
          {report.hit_rate != null && <> · попадание в зоны: <strong>{pct(report.hit_rate)}</strong></>}
          {report.last_sample_at && <span className='rk-muted'> · последний {fmtDateTime(report.last_sample_at)}</span>}</p>
      </>}
  </div>;
}

const zoneText = zones => (zones || []).map(zone => `места ${zone.pos_from ?? '…'}–${zone.pos_to ?? '…'}`
  + `${zone.bid_min != null || zone.bid_max != null ? `, ставки ${range(zone.bid_min, zone.bid_max)} ₽` : ''}`).join(' · ');

// Товары РК: у каждого свои зоны и свои ключевые запросы (в одной РК бывают разные товары со своими ставками и местами).
function ProductZones({ rows, zonesBySku, keysBySku, report, lastChanges, onChange }) {
  const [openSku, setOpenSku] = useState(null);
  const [tab, setTab] = useState('zones');
  const setZones = (sku, zones) => {
    const next = { ...zonesBySku };
    if (zones.length) next[sku] = zones; else delete next[sku];
    onChange({ product_zones: next });
  };
  const setKeys = (sku, keys) => {
    const next = { ...keysBySku };
    if (keys.length) next[sku] = keys; else delete next[sku];
    onChange({ product_keywords: next });
  };
  const copyZonesTo = sku => {
    const next = { ...zonesBySku };
    rows.forEach(row => { if (String(row.sku_id) !== sku) next[String(row.sku_id)] = clone(zonesBySku[sku]); });
    onChange({ product_zones: next });
  };
  return <div className='rk-table-wrap'><table aria-label='Товары РК и зоны' className='rk-products'>
    <thead><tr><th>Товар</th><th>Ставка</th><th>Главный ключ</th><th>Зоны</th><th>Замеры · попадание</th><th>Решение по ставке</th><th /></tr></thead>
    <tbody>{rows.flatMap(target => {
      const sku = String(target.sku_id);
      const zones = zonesBySku[sku] || [];
      const keys = keysBySku[sku] || [];
      const product = report?.products?.[sku];
      const decision = report?.hold?.pairs?.[sku];
      const change = lastChanges?.[target.key];
      const open = openSku === sku;
      const line = <tr key={sku} className={open ? 'is-open' : ''}>
        <td><strong>SKU {sku}</strong><span className='rk-products__name' title={target.product_name || ''}>{target.product_name || 'Товар без названия'}</span></td>
        <td>{number(target.bid)}{target.bid != null ? ' ₽' : ''}
          <small>{change ? `${fmtDateTime(change.at)} · ${change.who}` : 'не менялась'}</small></td>
        <td>{keys.length ? <>{keys[0]}{keys.length > 1 && <small>и ещё {keys.length - 1}</small>}</> : <span className='rk-muted'>не задан</span>}</td>
        <td>{zones.length ? zoneText(zones) : <span className='rk-muted'>зон нет — ставку ведёт тактика группы</span>}</td>
        <td>{product ? `${product.samples} · ${pct(product.hit_rate)}` : '—'}</td>
        <td>{decision ? <>{HOLD_ACTIONS[decision.action] || '—'}{decision.action === 'move' && decision.changed_to != null ? ` → ${number(decision.changed_to)} ₽` : ''}
          <small>{decision.reason}</small></> : '—'}</td>
        <td><button type='button' aria-expanded={open} aria-label={`Зоны товара ${sku}`}
          onClick={() => { setOpenSku(open ? null : sku); setTab('zones'); }}>{open ? 'Свернуть' : zones.length || keys.length ? 'Изменить' : 'Настроить'}</button></td>
      </tr>;
      if (!open) return [line];
      const notes = (product?.recommendations || []).length + (product?.key_notes || []).length;
      const tabs = [['zones', `Зоны${zones.length ? ` (${zones.length})` : ''}`], ['keys', `Ключи${keys.length ? ` (${keys.length})` : ''}`],
        ['report', `Замеры и рекомендации${notes ? ` (${notes})` : ''}`]];
      return [line, <tr key={`${sku}:edit`} className='rk-products__edit'><td colSpan={7}>
        <div role='group' aria-label={`Настройки товара ${sku}`}>
          <div className='oz-auto__tabs' role='tablist' aria-label={`Разделы товара ${sku}`}>
            {tabs.map(([key, label]) => <button key={key} type='button' role='tab' aria-selected={tab === key}
              className={tab === key ? 'is-active' : ''} onClick={() => setTab(key)}>{label}</button>)}
          </div>
          {tab === 'zones' && <div>
            <h6>Зоны товара SKU {sku}: диапазон ставок ↔ диапазон мест <small>до 3, сверху вниз по приоритету</small></h6>
            <Zones zones={zones} onChange={next => setZones(sku, next)} />
            {zones.length > 0 && rows.length > 1 && <button type='button' onClick={() => copyZonesTo(sku)}>Скопировать эти зоны на остальные товары РК</button>}
          </div>}
          {tab === 'keys' && <div>
            <h6>Ключевые запросы товара SKU {sku} <small>до 5: первый главный, по нему считаем место; остальные замеряем, вдруг один работает лучше</small></h6>
            <Keywords items={keys} max={5} mainFirst onChange={next => setKeys(sku, next)} />
            {keys.length > 1 && <p className='rk-note'>{SEVERAL_KEYS}</p>}
          </div>}
          {tab === 'report' && <div>
            {!product && <p className='rk-muted'>Замеров пока нет: агент меряет ключи раз в полчаса, пока он на связи.</p>}
            {(product?.key_notes || []).map(text => <p key={text} className='rk-note'>{text}</p>)}
            {(product?.recommendations || []).length > 0 && <ul className='rk-recommendations' aria-label='Рекомендации'>
              {product.recommendations.map(text => <li key={text}>{text}</li>)}</ul>}
            {product?.zones?.length > 0 && <table aria-label='Попадание по зонам'>
              <thead><tr><th>Зона</th><th>Доля замеров в зоне</th><th>Ставки аукциона в зоне</th><th>Места в зоне при ставке из диапазона</th></tr></thead>
              <tbody>{product.zones.map(zone => <tr key={zone.zone}>
                <td>Зона {zone.zone}</td><td>{pct(zone.share)}</td>
                <td>{zone.bids_in_zone ? `${number(zone.bids_in_zone.min)}–${number(zone.bids_in_zone.max)} ₽ (медиана ${number(zone.bids_in_zone.median)})` : '—'}</td>
                <td>{zone.p_given_bid_range == null ? '—' : `${pct(zone.p_given_bid_range)} из ${zone.samples_in_bid_range}`}</td>
              </tr>)}</tbody></table>}
            {(product?.keys || []).length > 0 && <table aria-label='Ключи товара'>
              <thead><tr><th>Ключ</th><th>Роль</th><th>Замеров</th><th>Медианное место</th><th>Показы / клики / заказы за 7 дней</th></tr></thead>
              <tbody>{product.keys.map(key => {
                const stat = (product.phrases || []).find(item => item.phrase.toLowerCase() === key.query.toLowerCase());
                return <tr key={key.query}>
                  <td>{key.query}</td><td>{key.role === 'main' ? 'главный' : 'вспомогательный'}</td><td>{key.samples}</td>
                  <td>{key.median_position ?? '—'}</td>
                  <td>{stat ? `${number(stat.views)} / ${number(stat.clicks)} / ${number(stat.orders)}${stat.ctr != null ? ` · CTR ${number(stat.ctr)}%` : ''}` : '—'}</td>
                </tr>;
              })}</tbody></table>}
          </div>}
        </div></td></tr>];
    })}</tbody>
  </table></div>;
}

// Полки по товарам: у каждого артикула свои фокусные запросы и свои конкуренты.
function ShelfProducts({ rows, productShelves, shelfRows, onChange }) {
  const [openSku, setOpenSku] = useState(null);
  const setEntry = (sku, patch) => {
    const current = productShelves[sku] || { keywords: [], competitors: [] };
    const merged = { ...current, ...patch };
    const next = { ...productShelves };
    if ((merged.keywords || []).length || (merged.competitors || []).length) next[sku] = merged; else delete next[sku];
    onChange(next);
  };
  const parse = text => text.split(/[\s,;]+/).filter(Boolean).map(Number).filter(Number.isFinite);
  return <div className='rk-table-wrap'><table aria-label='Товары РК и полки' className='rk-products'>
    <thead><tr><th>Товар</th><th>Ключи</th><th>Конкуренты</th><th>Замер</th><th /></tr></thead>
    <tbody>{rows.flatMap(target => {
      const sku = String(target.sku_id);
      const entry = productShelves[sku] || { keywords: [], competitors: [] };
      const mine = shelfRows.filter(row => String(row.sku_id) === sku);
      const best = mine.map(row => row.best_position).filter(value => value != null);
      const open = openSku === sku;
      const line = <tr key={sku} className={open ? 'is-open' : ''}>
        <td><strong>SKU {sku}</strong><span className='rk-products__name' title={target.product_name || ''}>{target.product_name || 'Товар без названия'}</span></td>
        <td>{entry.keywords.length ? entry.keywords.join(' · ') : <span className='rk-muted'>не заданы</span>}</td>
        <td>{entry.competitors.length ? `${entry.competitors.length} шт.` : <span className='rk-muted'>не заданы</span>}</td>
        <td>{mine.length ? `лучшая позиция ${best.length ? Math.min(...best) : '—'}` : <span className='rk-muted'>—</span>}</td>
        <td><button type='button' aria-expanded={open} aria-label={`Полки товара ${sku}`}
          onClick={() => setOpenSku(open ? null : sku)}>{open ? 'Свернуть' : entry.keywords.length || entry.competitors.length ? 'Изменить' : 'Настроить'}</button></td>
      </tr>;
      if (!open) return [line];
      return [line, <tr key={`${sku}:edit`} className='rk-products__edit'><td colSpan={5}>
        <div role='group' aria-label={`Полки товара ${sku}`}>
          <h6>Фокусные запросы товара SKU {sku} <small>до 3</small></h6>
          <Keywords items={entry.keywords} max={3} onChange={keywords => setEntry(sku, { keywords })} />
          <Field label='Прямые конкуренты этого товара, SKU (до 10, через запятую или пробел)' wide>
            <input aria-label='Конкуренты товара' defaultValue={entry.competitors.join(', ')}
              onBlur={e => setEntry(sku, { competitors: parse(e.target.value) })} />
          </Field>
          <ShelfView rows={mine} />
        </div></td></tr>];
    })}</tbody>
  </table></div>;
}

function ShelfView({ rows }) {
  if (!rows.length) return <p className='rk-muted'>Агент ещё не принёс замеров: задания ставятся раз в сутки на каждый фокусный запрос.</p>;
  return <table aria-label='Замеры полок'>
    <thead><tr><th>Запрос</th><th>Замер</th><th>Конкурентов</th><th>Наш SKU найден у</th><th>Лучшая позиция</th></tr></thead>
    <tbody>{rows.map(row => <tr key={`${row.campaign_id}|${row.query}`}>
      <td>{row.query}</td><td>{row.at ? fmtDateTime(row.at) : '—'}</td>
      <td>{row.reachable} из {row.competitors}{row.unreachable?.length ? ` (недоступны: ${row.unreachable.join(', ')})` : ''}</td>
      <td>{row.found_at}</td><td>{row.best_position ?? '—'}</td>
    </tr>)}</tbody>
  </table>;
}

function Pairs({ title, rows, lastChanges }) {
  return <table aria-label={`Пары: ${title}`}>
    <thead><tr><th>Товар</th><th>Ставка</th><th>Минимум Ozon</th><th>Последнее изменение</th><th>Готовность</th></tr></thead>
    <tbody>{rows.map(target => {
      const change = lastChanges?.[target.key];
      return <tr key={target.key}>
        <td>{target.product_name || 'Товар без названия'}</td>
        <td>{number(target.bid)}{target.bid != null ? ' ₽' : ''}</td>
        <td>{number(target.min_bid)}{target.min_bid != null ? ' ₽' : ''}</td>
        <td>{change ? `${fmtDateTime(change.at)} · ${change.who}${change.before != null ? ` · ${number(change.before)} → ${number(change.after)} ₽` : ''}` : 'не менялась'}</td>
        <td>{target.available ? 'Доступна' : (target.reasons || []).join('; ') || 'Недоступна'}</td>
      </tr>;
    })}</tbody>
  </table>;
}

// Правка одной РК: всё в одном месте, у каждого поля подпись.
function CampaignEditor({ cid, kind, name, value, agentOnline, shelfRows, positionReport, rows, lastChanges, onChange, onApplyToAll, onClose, sameTypeCount, onSave, busy, dirty, limits }) {
  const set = values => onChange({ ...value, ...values });
  return <div className='rk-editor' role='group' aria-label={`Настройки РК ${name || cid}`}>
    <div className='rk-editor__head'>
      <h5 className='rk-editor__title'>{name || `РК ${cid}`} <span className='rk-muted'>· {TYPES[kind].title}</span></h5>
      <button type='button' onClick={onClose}>Свернуть</button>
    </div>
    <div className='rk-grid'>
      {kind === 'search'
        ? <Field label='Режим'><select value={value.mode} onChange={e => set({ mode: e.target.value })}>
          <option value='AUTO'>Авто</option><option value='HOLD_POSITION'>Удержание позиции</option></select></Field>
        : <Field label='Режим'><select value='AUTO' disabled><option value='AUTO'>Авто</option></select></Field>}
      <LimitFields block={value} onChange={set} />
    </div>
    {kind === 'search' && value.mode === 'HOLD_POSITION' && <>
      <section><h6>Товары РК <small>ключевой запрос и зоны задаются на товар; товар без зон удержание не трогает</small></h6>
        <ProductZones rows={rows} zonesBySku={value.product_zones || {}} keysBySku={value.product_keywords || {}} report={positionReport}
          lastChanges={lastChanges} onChange={set} /></section>
      <section><h6>Решения по ставке <small>по замерам главного ключа; ставку ведёт это правило, пока обход был не позже часа назад, потом РК снова в «Авто»</small></h6>
        <Check checked={value.hold_send !== false} onChange={e => set({ hold_send: e.target.checked })}
          hint='Выключено — решения только пишутся в журнал как «сделал бы», ставка не меняется.'>Менять ставку</Check>
        <Check checked={Boolean(value.hold_ignore_rails)}
          onChange={e => set({ hold_ignore_rails: e.target.checked, hold_ignore_ack: e.target.checked ? value.hold_ignore_ack : false })}
          hint='Ставка будет расти по зонам, даже когда её запрещают ограничители по прибыли и ДРР. Выключено — они действуют.'>Игнорировать ограничители</Check>
        {value.hold_ignore_rails && <div className='rk-ignore' role='group' aria-label='Какие ограничители игнорируются'>
          <strong>Не будут действовать:</strong>
          <ul>
            <li>Минимальная рентабельность группы{limits?.min_margin_pct != null ? ` (сейчас ${number(limits.min_margin_pct)}%)` : ''}: ставку нельзя поднимать, когда рентабельность за 7 дней ниже.</li>
            <li>Правило «цель — прибыль»: подъём запрещён, если добавленная реклама за 7 дней уменьшила прибыль или прибыль неизвестна.</li>
            <li>Предельный ДРР: рекламный {value.drr_ad_max === '' || value.drr_ad_max == null ? 'не задан' : `${number(value.drr_ad_max)}%`}, общий {value.drr_total_max === '' || value.drr_total_max == null ? 'не задан' : `${number(value.drr_total_max)}%`} этой РК и предельный ДРР дня группы{limits?.target_drr != null ? ` (сейчас ${number(limits.target_drr * 100)}%)` : ''}.</li>
          </ul>
          <span>Остаются в силе: диапазон ставок РК и диапазон ставок зоны, максимальная ставка группы{limits?.max_bid != null ? ` (${number(limits.max_bid)} ₽)` : ''}, остаток товара, минимум Ozon, шаг и стоп «подъём без отдачи по месту».</span>
          <Check checked={Boolean(value.hold_ignore_ack)} onChange={e => set({ hold_ignore_ack: e.target.checked })}>
            Я согласен(на): ставка может расти, даже если реклама убыточна или ДРР выше предельного. Без этого настройки не сохранятся.</Check>
          <small>Если не согласны, вместо галочки поправьте сами ограничители вручную: «Настройки группы» → минимальная рентабельность, предельный ДРР; здесь же — предельный ДРР РК и диапазон ставок.</small>
        </div>}
        <div className='rk-grid'>
          <Field label='Шаг ставки, % за раз'><input type='number' min='1' max='100' aria-label='Шаг ставки, % за раз'
            value={value.hold_step_pct ?? 20} onChange={e => set({ hold_step_pct: e.target.value })} /></Field>
          <Field label='Окно решения, замеров'><input type='number' min='3' max='24' aria-label='Окно решения, замеров'
            value={value.hold_window ?? 6} onChange={e => set({ hold_window: e.target.value })} /></Field>
        </div>
        <p className='rk-note'>Один замер не двигает ставку: решение берётся по окну (6 замеров — три часа). Шаг не больше заданного, после смены ждём повторный замер.</p></section>
      <section><h6>Замеры и рекомендации</h6><PositionReport report={positionReport} agentOnline={agentOnline} /></section>
    </>}
    {kind === 'rec' && <section>
      <Check checked={value.shelves.enabled} onChange={e => set({ shelves: { ...value.shelves, enabled: e.target.checked } })}
        hint='Агент по фокусным запросам товара находит его конкурентов в выдаче, заходит на их карточки и ищет наш товар в рекомендательных полках. Ключи и конкуренты задаются на каждый товар.'>
        Учитывать место в рекомендательных полках</Check>
      {value.shelves.enabled && <>
        {!agentOnline && <p className='rk-note is-action'>Локальный агент не на связи: замер полок выключен, пока он не вернётся. Ставки работают без полок.</p>}
        <h6>Товары РК <small>ключи (до 3) и конкуренты (до 10) задаются на товар; товар без них не замеряется</small></h6>
        <ShelfProducts rows={rows} productShelves={value.product_shelves || {}} shelfRows={shelfRows}
          onChange={product_shelves => set({ product_shelves })} />
      </>}
    </section>}
    {!(kind === 'search' && value.mode === 'HOLD_POSITION') && <details className='rk-pairs'><summary>Товары РК ({rows.length})</summary>
      <Pairs title={name || cid} rows={rows} lastChanges={lastChanges} /></details>}
    <div className='rk-editor__foot'>
      <button type='button' className='oz-auto__primary' disabled={busy} onClick={onSave}>{busy ? 'Сохраняем…' : 'Сохранить настройки'}</button>
      {dirty && <span className='rk-muted'>Есть несохранённые изменения</span>}
      {sameTypeCount > 1 && <button type='button' onClick={onApplyToAll}
        title='Не сохраняет, а копирует настройки этой РК в остальные РК типа; сохраняет кнопка «Сохранить настройки»'>
        Скопировать эти настройки на остальные РК типа «{TYPES[kind].title}» ({sameTypeCount - 1})</button>}
    </div>
  </div>;
}

// Массовая правка выбранных РК: пустое поле — «не менять». Ключи, зоны и полки у товаров свои, их массово не копируют.
function BulkPanel({ kind, count, sources, onApply, onCancel }) {
  const [values, setValues] = useState({ mode: '', bid_min: '', bid_max: '', drr_ad_max: '', drr_total_max: '', copy: '' });
  const set = key => e => setValues(current => ({ ...current, [key]: e.target.value }));
  const filled = Object.values(values).some(value => value !== '');
  return <div className='rk-bulk' role='group' aria-label='Массовая правка РК'>
    <strong>Выбрано РК: {count}. Пустое поле — не менять.</strong>
    <div className='rk-grid'>
      {kind === 'search' && <Field label='Режим'><select value={values.mode} onChange={set('mode')}>
        <option value=''>не менять</option><option value='AUTO'>Авто</option><option value='HOLD_POSITION'>Удержание позиции</option></select></Field>}
      <Field label='Минимальная ставка, ₽'><input type='number' min='0' value={values.bid_min} onChange={set('bid_min')} placeholder='не менять' /></Field>
      <Field label='Максимальная ставка, ₽'><input type='number' min='0' value={values.bid_max} onChange={set('bid_max')} placeholder='не менять' /></Field>
      <Field label='Предельный ДРР рекламный, %'><input type='number' min='0' value={values.drr_ad_max} onChange={set('drr_ad_max')} placeholder='не менять' /></Field>
      <Field label='Предельный ДРР общий, %'><input type='number' min='0' value={values.drr_total_max} onChange={set('drr_total_max')} placeholder='не менять' /></Field>

    </div>
    <div className='rk-bulk__actions'>
      <button type='button' className='oz-auto__primary' disabled={!filled} onClick={() => onApply(values)}>Применить к выбранным ({count})</button>
      <button type='button' onClick={onCancel}>Отмена</button>
    </div>
  </div>;
}

function LinkTable({ rows, labels }) {
  if (!rows.length) return <p className='rk-muted'>Нет артикулов, у которых есть и «Поиск», и «Поиск и Рекомендации».</p>;
  return <table aria-label='Связка по артикулам'>
    <thead><tr><th>Артикул</th><th>CTR «Поиска»</th><th>CTR «Поиска и Рекомендаций»</th><th>Статус</th><th>Что делает система</th></tr></thead>
    <tbody>{rows.map(row => {
      const [label, tone] = STATUS[row.status] || [row.status, 'muted'];
      return <tr key={row.sku_id}>
        <td>{row.sku_id}<small>{row.search_campaign} · {row.rec_campaign}</small></td>
        <td>{number(row.ctr_search_before)}% → {number(row.ctr_search)}%</td>
        <td>{number(row.ctr_rec_before)}% → {number(row.ctr_rec)}%</td>
        <td className={`is-${tone}`}>{label}<small>{row.note}</small></td>
        <td>{row.action ? (labels?.[row.action.action] || row.action.action)
          : row.status === 'cannibalism' && !row.enabled ? 'Антиканнибализм выключен' : '—'}</td>
      </tr>;
    })}</tbody>
  </table>;
}

// Кампании типа: уникальные по номеру кампании в Ozon, с их парами «РК–товар».
export const campaignsOf = (targets, kind) => {
  const byId = new Map();
  (targets || []).filter(target => target.placement === TYPES[kind].placement).forEach(target => {
    const id = String(target.ozon_campaign_id ?? target.campaign_id);
    if (!byId.has(id)) byId.set(id, { id, name: target.campaign_name, rows: [] });
    byId.get(id).rows.push(target);
  });
  // Целевой расход, автостратегии и «вывод в топ»: ставкой управляет сам Ozon, ни режимы, ни лимиты, ни зоны, ни полки не применяются.
  return [...byId.values()].map(campaign => {
    const managed = campaign.rows.length > 0 && campaign.rows.every(row => row.ozon_managed);
    return { ...campaign, ozonManaged: managed, strategy: managed ? campaign.rows[0].auto_strategy : null };
  });
};

const OZON_STRATEGIES = {
  TARGET_CIR: 'Целевой расход', MAX_CLICKS: 'Автостратегия', TOP_MAX_CLICKS: 'Автостратегия', TOP_PROMOTION: 'Вывод в топ',
};
const strategyName = strategy => OZON_STRATEGIES[strategy] || 'Стратегия Ozon';

// Чего не хватает режиму «Удержание позиции», чтобы его приняли: видно рядом с кнопкой «Сохранить», а не только после отказа сервера.
function problemsOf(form) {
  return Object.entries(form.campaigns || {}).flatMap(([cid, entry]) => {
    if (entry.type !== 'search' || entry.mode !== 'HOLD_POSITION') return [];
    const zones = Object.keys(entry.product_zones || {});
    if (!zones.length) return [`РК ${cid}: задайте зону хотя бы одному товару`];
    return zones.filter(sku => !(entry.product_keywords?.[sku] || []).length)
      .map(sku => `РК ${cid}: у товара ${sku} задайте ключевой запрос`);
  });
}

function zonesSummary(value, total) {
  if (value.mode !== 'HOLD_POSITION') return '—';
  const held = Object.keys(value.product_zones || {}).length;
  return held ? `товаров с зонами: ${held} из ${total}` : 'зоны не заданы';
}

function shelvesSummary(value, shelfRows, total) {
  if (!value.shelves?.enabled) return 'не учитываются';
  const set = Object.values(value.product_shelves || {}).filter(entry => entry.keywords?.length && entry.competitors?.length).length;
  const best = shelfRows.length ? Math.min(...shelfRows.map(row => row.best_position ?? Infinity)) : null;
  return `товаров с полками: ${set} из ${total}${best != null && Number.isFinite(best) ? ` · лучшая позиция ${best}` : ''}`;
}

export default function OzonAutomationRk({ base, request, targets, lastChanges }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState({ search: [], rec: [] });
  const [bulkKind, setBulkKind] = useState(null);

  const apply = payload => { setData(payload); setForm(toForm(payload.settings)); setDirty(false); };
  // `request` приложения пересоздаётся при каждом обновлении сессии: грузим по смене адреса, а не функции,
  // иначе обновление токена стирало несохранённые правки и гасило кнопку «Сохранить».
  const requestRef = useRef(request);
  requestRef.current = request;
  const load = useCallback(async () => {
    if (!requestRef.current) return;
    try { apply(await requestRef.current(`${base}/rk`)); setError(''); } catch (err) { setError(failure(err)); }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      apply(await requestRef.current(`${base}/rk`, { method: 'PUT', body: JSON.stringify({ settings: toWire(form) }) }));
      setSaved(true);
    } catch (err) { setError(failure(err)); } finally { setBusy(false); }
  };

  const touch = update => { setSaved(false); setDirty(true); setForm(update); };
  const valueOf = (cid, kind) => {
    const entry = form.campaigns[cid];
    return entry && entry.type === kind ? { ...entry, __own: true } : { ...clone(form[kind]), __own: false };
  };
  const setCampaign = (cid, kind, value) => touch(current => {
    const { __own, ...clean } = value;
    return { ...current, campaigns: { ...current.campaigns, [cid]: { ...clean, type: kind } } };
  });
  // Копируются настройки самой РК: режим, лимиты, параметры решений, галочка полок. Зоны, ключи и полки у товаров свои:
  // их номера SKU в другой РК ничего не значат, поэтому у каждой РК остаются её собственные.
  const applyToAll = (kind, source) => touch(current => {
    const { __own, ...clean } = source;
    const shared = ['mode', 'bid_min', 'bid_max', 'drr_ad_max', 'drr_total_max', 'hold_send', 'hold_step_pct', 'hold_window',
      'hold_ignore_rails', 'hold_ignore_ack'].filter(key => key in clean);
    const campaigns = { ...current.campaigns };
    campaignsOf(targets, kind).filter(campaign => !campaign.ozonManaged).forEach(campaign => {
      const own = current.campaigns[campaign.id]?.type === kind ? current.campaigns[campaign.id] : clone(current[kind]);
      const next = { ...own, type: kind };
      shared.forEach(key => { next[key] = clean[key]; });
      if (kind === 'rec') next.shelves = { ...(own.shelves || {}), enabled: Boolean(clean.shelves?.enabled) };
      campaigns[campaign.id] = next;
    });
    return { ...current, campaigns };
  });
  const applyBulk = (kind, values) => touch(current => {
    const campaigns = { ...current.campaigns };
        selected[kind].forEach(cid => {
      const entry = current.campaigns[cid]?.type === kind ? clone(current.campaigns[cid]) : clone(current[kind]);
      ['bid_min', 'bid_max', 'drr_ad_max', 'drr_total_max'].forEach(key => { if (values[key] !== '') entry[key] = values[key]; });
      if (kind === 'search' && values.mode) entry.mode = values.mode;
      campaigns[cid] = { ...entry, type: kind };
    });
    return { ...current, campaigns };
  });
  const patchAnti = values => touch(current => ({ ...current, anticannibal: { ...current.anticannibal, ...values } }));
  const moveAction = (index, delta) => {
    const order = [...form.anticannibal.order];
    const [moved] = order.splice(index, 1);
    order.splice(index + delta, 0, moved);
    patchAnti({ order });
  };
  const toggleSelect = (kind, cid) => setSelected(current => ({
    ...current, [kind]: current[kind].includes(cid) ? current[kind].filter(id => id !== cid) : [...current[kind], cid] }));

  const renderType = (kind, { title }) => {
    const campaigns = campaignsOf(targets, kind);
    const own = campaigns.filter(campaign => !campaign.ozonManaged);
    const ids = own.map(campaign => campaign.id);
    const chosen = selected[kind].filter(id => ids.includes(id));
    return <section key={kind} className='rk-section' aria-label={title}>
      <div className='rk-section__head'>
        <h4>{title}</h4><span className='rk-muted'>РК: {campaigns.length}{own.length < campaigns.length ? ` · ведёт Ozon: ${campaigns.length - own.length}` : ''}</span>
        {own.length > 0 && <div className='rk-section__tools'>
          <button type='button' onClick={() => setSelected(current => ({ ...current, [kind]: chosen.length === ids.length ? [] : ids }))}>
            {chosen.length === ids.length ? 'Снять выбор' : 'Выбрать все'}</button>
          <button type='button' className='oz-auto__primary' disabled={!chosen.length} onClick={() => setBulkKind(kind)}>
            Изменить выбранные ({chosen.length})</button>
        </div>}
      </div>
      {bulkKind === kind && <BulkPanel kind={kind} count={chosen.length} sources={own.filter(item => form.campaigns[item.id]?.type === kind)}
        onApply={values => { applyBulk(kind, values); setBulkKind(null); }} onCancel={() => setBulkKind(null)} />}
      {campaigns.length === 0
        ? <p className='rk-muted'>В группе нет кампаний этого типа.</p>
        : <div className='rk-table-wrap'><table className='rk-table' aria-label={`РК: ${title}`}>
          <thead><tr>
            <th className='rk-check-col' />
            <th>РК</th><th>Режим</th><th>Ставка, ₽</th><th>ДРР рекл. / общ., %</th>
            <th>{kind === 'search' ? 'Зоны по товарам' : 'Полки'}</th>
            {kind === 'search' && <><th>Замеров</th><th>Попадание</th></>}
            <th>Рекомендации</th><th />
          </tr></thead>
          <tbody>{campaigns.map(campaign => {
            if (campaign.ozonManaged) {
              return <tr key={campaign.id} className='is-managed'>
                <td className='rk-check-col' />
                <td><strong>{campaign.name || `РК ${campaign.id}`}</strong><small>{strategyName(campaign.strategy)} · товаров: {campaign.rows.length}</small></td>
                <td colSpan={kind === 'search' ? 8 : 6}><span className='rk-muted'>Не настраивается: ставкой управляет Ozon ({strategyName(campaign.strategy).toLowerCase()}).
                  Режимы, лимиты, зоны, ключи и полки к такой РК не применяются.</span></td>
              </tr>;
            }
            const value = valueOf(campaign.id, kind);
            const report = (data.position_report || {})[campaign.id];
            const shelfRows = (data.shelf_view || []).filter(row => String(row.campaign_id) === campaign.id);
            const notes = report ? (report.recommendations || []).length + (report.key_notes || []).length : 0;
            const open = openId === `${kind}:${campaign.id}`;
            return <tr key={campaign.id} className={open ? 'is-open' : ''}>
                <td className='rk-check-col'><input type='checkbox' aria-label={`Выбрать РК ${campaign.name || campaign.id}`}
                  checked={chosen.includes(campaign.id)} onChange={() => toggleSelect(kind, campaign.id)} /></td>
                <td><strong>{campaign.name || `РК ${campaign.id}`}</strong>
                  <small>{value.__own ? 'свои настройки' : 'по умолчанию типа'} · товаров: {campaign.rows.length}</small></td>
                <td>{kind === 'search' && value.mode === 'HOLD_POSITION' ? 'Удержание позиции' : 'Авто'}</td>
                <td>{range(num(value.bid_min), num(value.bid_max))}</td>
                <td>{value.drr_ad_max === '' || value.drr_ad_max == null ? '—' : number(value.drr_ad_max)} / {value.drr_total_max === '' || value.drr_total_max == null ? '—' : number(value.drr_total_max)}</td>
                <td>{kind === 'search' ? zonesSummary(value, campaign.rows.length) : shelvesSummary(value, shelfRows, campaign.rows.length)}</td>
                {kind === 'search' && <><td>{report ? report.samples : '—'}</td><td>{report ? pct(report.hit_rate) : '—'}</td></>}
                <td>{notes > 0 ? <span className='rk-badge'>{notes} рекомендац.</span> : <span className='rk-muted'>—</span>}</td>
                <td><button type='button' aria-expanded={open} aria-label={`Настроить РК ${campaign.name || campaign.id}`}
                  onClick={() => setOpenId(open ? null : `${kind}:${campaign.id}`)}>{open ? 'Свернуть' : 'Настроить'}</button></td>
            </tr>;
          })}</tbody>
        </table></div>}
      {own.filter(campaign => openId === `${kind}:${campaign.id}`).map(campaign => <CampaignEditor key={campaign.id}
        cid={campaign.id} kind={kind} name={campaign.name} value={valueOf(campaign.id, kind)} agentOnline={data.agent_online}
        shelfRows={(data.shelf_view || []).filter(row => String(row.campaign_id) === campaign.id)}
        positionReport={(data.position_report || {})[campaign.id]} rows={campaign.rows} lastChanges={lastChanges}
        sameTypeCount={own.length} onClose={() => setOpenId(null)} onSave={save} busy={busy} dirty={dirty} limits={data.limits}
        onChange={next => setCampaign(campaign.id, kind, next)}
        onApplyToAll={() => applyToAll(kind, valueOf(campaign.id, kind))} />)}
    </section>;
  };

  return <div className='rk'>
    {!form && error && <p role='alert' className='bidder__error'>{error}</p>}
    {form && <>
      {Object.entries(TYPES).map(([kind, meta]) => renderType(kind, meta))}

      <section className='rk-section' aria-label='Антиканнибализм'>
        <div className='rk-section__head'><h4>Антиканнибализм: «Поиск» + «Поиск и Рекомендации» по артикулу</h4></div>
        <Check checked={form.anticannibal.enabled} onChange={e => patchAnti({ enabled: e.target.checked })}
          hint='Чтобы «Поиск и Рекомендации» не отбирали трафик у «Поиска». Включённый, он важнее режима «Авто».'>
          Включить «Антиканнибализм»</Check>
        <div className='rk-grid'>
          <Field label='CTR «Внимание» от, %'><input type='number' min='0' step='0.1' value={form.anticannibal.ctr_attention_pct}
            onChange={e => patchAnti({ ctr_attention_pct: Number(e.target.value) })} /></Field>
          <Field label='CTR выше верхнего порога, %'><input type='number' min='0' step='0.1' value={form.anticannibal.ctr_alarm_pct}
            onChange={e => patchAnti({ ctr_alarm_pct: Number(e.target.value) })} /></Field>
        </div>
        <h6>Очерёдность действий при каннибализме <small>сверху вниз: следующее включается, когда предыдущее исчерпано</small></h6>
        <ol className='rk-actions-list'>{form.anticannibal.order.map((action, index) => <li key={action}>
          <span>{data.action_labels?.[action] || action}</span>
          <button type='button' disabled={index === 0} onClick={() => moveAction(index, -1)} aria-label={`Выше: ${action}`}>↑</button>
          <button type='button' disabled={index === form.anticannibal.order.length - 1} onClick={() => moveAction(index, 1)} aria-label={`Ниже: ${action}`}>↓</button>
        </li>)}</ol>
        <p className='rk-muted'>Кампания «Поиск и Рекомендации», где несколько товаров, не выключается: о ней придёт уведомление.</p>
        <LinkTable rows={data.link || []} labels={data.action_labels} />
      </section>

      <div className='rk-savebar'>
        <button type='button' className='oz-auto__primary' disabled={busy} onClick={save}>{busy ? 'Сохраняем…' : 'Сохранить настройки'}</button>
        {dirty ? <span className='rk-muted'>Есть несохранённые изменения</span> : !saved && <span className='rk-muted'>Изменений пока нет</span>}
        {saved && <span className='oz-auto__ok' role='status'>Сохранено</span>}
        {problemsOf(form).map(text => <span key={text} className='rk-note is-action'>{text}</span>)}
        {error && <span role='alert' className='bidder__error'>{error}</span>}
      </div>
    </>}
    {!request && Object.entries(TYPES).map(([kind, { title }]) => <section key={kind} className='rk-section' aria-label={title}>
      <div className='rk-section__head'><h4>{title}</h4><span className='rk-muted'>РК: {campaignsOf(targets, kind).length}</span></div>
      {campaignsOf(targets, kind).map(campaign => <div key={campaign.id}>
        <strong>{campaign.name || campaign.id}</strong>
        <Pairs title={campaign.name || campaign.id} rows={campaign.rows} lastChanges={lastChanges} />
      </div>)}
    </section>)}
  </div>;
}
